import {
  Button,
  Input,
  InputType,
  Typography,
} from '../../components/primitives';
import Alert from '../../components/ui/Alert';
import ContactSupport from '../../components/ui/ContactSupport';
import { PasswordStrengthIndicator } from '../../components/ui/PasswordStrengthIndicator';
import { RequiredFieldIndicator } from '../../components/ui/RequiredFieldIndicator';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../components/ui/form';
import { Icons } from '../../icons';
import { testId } from '../../utils';
import { PASSWORD_MAX, PASSWORD_MIN } from '../../utils/constants';
import { PageCard } from './PageCard';
import { zodResolver } from '@hookform/resolvers/zod';
import { Cornerstone } from '@rpi/api';
import { useTranslation, Trans } from '@rpi/i18n';
import React, { useCallback, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

export const ConfirmForgotPasswordRoute: React.FC = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.auth' });
  const { token } = useParams();

  const ConfirmForgotPasswordSchema = z
    .object({
      token: z.string(),
      password: z
        .string()
        .min(PASSWORD_MIN, `${t('minPassword', { minChar: PASSWORD_MIN })}`),
    })
    .required();
  type ConfirmForgotPasswordSchema = z.infer<
    typeof ConfirmForgotPasswordSchema
  >;

  const navigate = useNavigate();

  const form = useForm<ConfirmForgotPasswordSchema>({
    resolver: zodResolver(ConfirmForgotPasswordSchema),
    defaultValues: {
      token: token ?? '',
      password: '',
    },
  });

  const { isError, isLoading, error, mutate } =
    Cornerstone.useConfirmForgotPassword({
      mutation: {
        onSuccess() {
          navigate('/auth/sign-in?resetPassword=true');
        },
        onError() {},
      },
    });

  const onValid = useCallback<SubmitHandler<ConfirmForgotPasswordSchema>>(
    (data) => {
      mutate({
        data: {
          token: data.token,
          newPassword: data.password,
        },
      });
    },
    [mutate],
  );
  const mutationData = error?.response?.data as any;

  const alertText = useMemo(() => {
    if (
      mutationData?.errorCode ===
      Cornerstone.ErrorCode.FORGOT_PASSWORD_VALIDATION
    ) {
      return t('forgot_password_expired');
    }

    if (isError) return t('generic_error');
    return null;
  }, [isError, mutationData, t]);

  return (
    <PageCard>
      <Form {...form}>
        <form
          noValidate
          onSubmit={form.handleSubmit(onValid)}
          className="flex flex-col gap-6"
        >
          {isError && (
            <Alert {...testId('alert')} variant="error">
              {alertText}
            </Alert>
          )}
          <Typography variant="title-default" className="text-center">
            {t('create_password_title')}
          </Typography>
          <RequiredFieldIndicator />
          <FormField
            control={form.control}
            name="password"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel {...testId('password', 'label')}>
                  <Typography variant="form-label">
                    <Trans
                      i18nKey="route.auth.password"
                      components={{
                        'required-indicator': <Icons.Indicator />,
                      }}
                    />
                  </Typography>
                </FormLabel>
                <FormControl>
                  <Input
                    {...testId('password')}
                    type={InputType.PASSWORD}
                    placeholder=""
                    {...field}
                    maxLength={PASSWORD_MAX}
                    invalid={fieldState.invalid}
                  />
                </FormControl>
                <FormMessage {...testId('password', 'message')} />
                <PasswordStrengthIndicator
                  password={form.getValues().password}
                />
              </FormItem>
            )}
          />
          <Button {...testId('submit')} type="submit" loading={isLoading}>
            {t('save_password')}
          </Button>
          <ContactSupport />
        </form>
      </Form>
    </PageCard>
  );
};
